/**
 * usePmPipelineWrites — the Task Pipeline writeback audit log + status summary.
 *
 * Admin-only endpoints: the backend returns 403 for non-admins, and this hook
 * propagates the detail string for the page to render.
 *
 * Both requests are issued together because the page header needs the summary
 * (including whether writeback is actually enabled) alongside the first table
 * page; a failure in either surfaces as one error.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  ListPmPipelineWritesParams,
  pmPipelineWrites,
} from '@/helpers/ApiHelper';
import type {
  PaginatedPmPipelineWrites,
  PmPipelineWriteSummary,
} from '@/types/pm_notification';

interface UsePmPipelineWritesResult {
  data: PaginatedPmPipelineWrites | null;
  summary: PmPipelineWriteSummary | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePmPipelineWrites(
  params: ListPmPipelineWritesParams = {},
): UsePmPipelineWritesResult {
  const [data, setData] = useState<PaginatedPmPipelineWrites | null>(null);
  const [summary, setSummary] = useState<PmPipelineWriteSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  // Destructured so the dep array stays stable without the caller memoizing.
  const { status, notion_id, page } = params;

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const [rows, counts] = await Promise.all([
          pmPipelineWrites.list({ status, notion_id, page }),
          pmPipelineWrites.summary(),
        ]);
        if (!cancelled) {
          setData(rows);
          setSummary(counts);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof ApiError ? e.detail : 'Failed to load pipeline writes',
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, notion_id, page, tick]);

  return { data, summary, isLoading, error, refetch };
}
